import React, { useState } from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import {
  Add,
  LocationOn,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  addJobAsync,
  moveCandidateStageAsync,
  addCandidateAsync,
} from "../../store/slices/dataSlice";
import { showSnackbar } from "../../store/slices/uiSlice";
import { ROLES_CONFIG, Job } from "../../types";
import StatCard from "../../components/shared/StatCard";
import StatusBadge from "../../components/shared/StatusBadge";


const STAGES = [
  "Screening",
  "Shortlisted",
  "Interview",
  "Offer",
  "Hired",
  "Rejected",
];
const DEPTS = [
  "Engineering",
  "Sales",
  "Marketing",
  "Finance",
  "HR",
  "Operations",
];
const LEVELS = ["Junior", "Mid", "Senior", "Lead"];

const STAGE_COLORS: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  Screening: {
    bg: "rgba(133,209,219,0.08)",
    text: "#6BB8C3",
    border: "#85D1DB",
  },
  Shortlisted: { bg: "#C9FDF2", text: "#6BB8C3", border: "#85D1DB" },
  Interview: { bg: "#fef3c7", text: "#d97706", border: "#f59e0b" },
  Offer: { bg: "#dcfce7", text: "#16a34a", border: "#22c55e" },
  Hired: { bg: "#ccfbf1", text: "#0d9488", border: "#B6F2D1" },
  Rejected: { bg: "#fee2e2", text: "#dc2626", border: "#ef4444" },
};

// Using global theme for table headers

const Recruitment: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const jobs = useAppSelector((s) => s.data.jobs);
  const candidates = useAppSelector((s) => s.data.candidates);
  const roleConfig = user
    ? ROLES_CONFIG[user.role.toLowerCase() as keyof typeof ROLES_CONFIG]
    : null;
  const canPostJob = roleConfig?.canPostJob ?? false;

  const openPositions = jobs.length;
  const applications = candidates.length;
  const inInterview = candidates.filter((c) => c.stage === 'Interview').length;
  const hired = candidates.filter((c) => c.stage === 'Hired').length;

  const dynamicStats = [
    {
      label: "Open Positions",
      value: openPositions.toString(),
      icon: "Work",
      delta: "Current open jobs",
      deltaType: openPositions > 0 ? "up" as const : "neutral" as const,
      color: "#85D1DB",
      iconBg: "rgba(133,209,219,0.1)",
    },
    {
      label: "Applications",
      value: applications.toString(),
      icon: "People",
      delta: "Total received",
      deltaType: applications > 0 ? "up" as const : "neutral" as const,
      color: "#6BB8C3",
      iconBg: "#C9FDF2",
    },
    {
      label: "In Interview",
      value: inInterview.toString(),
      icon: "Chat",
      delta: "In progress",
      deltaType: inInterview > 0 ? "up" as const : "neutral" as const,
      color: "#d97706",
      iconBg: "#fef3c7",
    },
    {
      label: "Hired (Total)",
      value: hired.toString(),
      icon: "HowToReg",
      delta: "Successfully onboarded",
      deltaType: hired > 0 ? "up" as const : "neutral" as const,
      color: "#16a34a",
      iconBg: "#dcfce7",
    },
  ];

  const [showAddJob, setShowAddJob] = useState(false);
  const [jobForm, setJobForm] = useState({
    title: "",
    dept: "Engineering",
    level: "Mid",
    location: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [candidateForm, setCandidateForm] = useState({
    jobId: "",
    name: "",
    score: 0
  });

  const handlePostJob = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (!jobForm.title) {
      newErrors.title = "Job Title is required";
      isValid = false;
    } else if (jobForm.title.trim().length < 3) {
      newErrors.title = "Min 3 characters";
      isValid = false;
    }

    if (!jobForm.location) {
      newErrors.location = "Location is required";
      isValid = false;
    } else if (jobForm.location.trim().length < 2) {
      newErrors.location = "Min 2 characters";
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) return;
    const newJob: Omit<Job, 'id'> = {
      ...jobForm,
      posted: new Date().toISOString().split('T')[0],
      applicants: 0,
      status: "Active",
      stage: "Screening",
    };
    
    dispatch(addJobAsync(newJob))
      .unwrap()
      .then(() => {
        dispatch(showSnackbar({ message: "Job posted successfully!" }));
        setShowAddJob(false);
        setJobForm({ title: "", dept: "Engineering", level: "Mid", location: "" });
        setErrors({});
      })
      .catch((err) => {
        dispatch(showSnackbar({ message: err || "Failed to post job", severity: "error" }));
      });
  };

  const moveStage = (id: string, dir: 1 | -1) => {
    const c = candidates.find((c) => c.id === id);
    if (!c) return;
    const idx = STAGES.indexOf(c.stage);
    const next = STAGES[Math.min(Math.max(idx + dir, 0), STAGES.length - 1)];
    if (next !== c.stage) {
      dispatch(moveCandidateStageAsync({ id, stage: next }));
      dispatch(
        showSnackbar({
          message: `${c.name} moved to ${next}`,
          severity: "info",
        }),
      );
    }
  };

  const handleAddCandidate = () => {
    if (!candidateForm.jobId || !candidateForm.name || candidateForm.score < 0 || candidateForm.score > 100) {
      dispatch(showSnackbar({ message: "Please fill all fields correctly", severity: "error" }));
      return;
    }
    const selectedJob = jobs.find(j => (j as any)._id === candidateForm.jobId || j.id === candidateForm.jobId);
    if (!selectedJob) return;

    const payload = {
      jobId: (selectedJob as any)._id || selectedJob.id,
      name: candidateForm.name,
      role: selectedJob.title,
      stage: "Screening",
      score: candidateForm.score
    };

    dispatch(addCandidateAsync(payload))
      .unwrap()
      .then(() => {
        dispatch(showSnackbar({ message: "Candidate added successfully!" }));
        setShowAddCandidate(false);
        setCandidateForm({ jobId: "", name: "", score: 0 });
      })
      .catch((err) => {
        dispatch(showSnackbar({ message: err || "Failed to add candidate", severity: "error" }));
      });
  };

  return (
    <Box>
      <SEO title="Recruitment" noindex />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {dynamicStats.map((s) => (
          <Grid item xs={12} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* Jobs Table */}
      <Card sx={{ mb: 2.5, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "14px 18px",
            borderBottom: "1px solid #e5e7eb",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            Open Positions
          </Typography>
          {canPostJob && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowAddJob(!showAddJob)}
              sx={{ fontSize: 12 }}
            >
              Post Job
            </Button>
          )}
        </Box>

        {showAddJob && (
          <Box
            sx={{
              p: 2,
              background: "#f8fafc",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Job Title"
                  value={jobForm.title}
                  onChange={(e) => {
                    setJobForm((f) => ({ ...f, title: e.target.value }));
                    if (errors.title)
                      setErrors((err) => ({ ...err, title: "" }));
                  }}
                  placeholder="Senior Developer"
                  error={!!errors.title}
                  helperText={errors.title}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={jobForm.dept}
                    label="Department"
                    onChange={(e) =>
                      setJobForm((f) => ({ ...f, dept: e.target.value }))
                    }
                  >
                    {DEPTS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={jobForm.level}
                    label="Level"
                    onChange={(e) =>
                      setJobForm((f) => ({ ...f, level: e.target.value }))
                    }
                  >
                    {LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Location"
                  value={jobForm.location}
                  onChange={(e) => {
                    setJobForm((f) => ({ ...f, location: e.target.value }));
                    if (errors.location)
                      setErrors((err) => ({ ...err, location: "" }));
                  }}
                  placeholder="Remote"
                  error={!!errors.location}
                  helperText={errors.location}
                />
              </Grid>
              <Grid item xs={12} sm={2} sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handlePostJob}
                  sx={{ flex: 1 }}
                >
                  Post
                </Button>
                <Button variant="outlined" onClick={() => setShowAddJob(false)}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "Job Title",
                  "Department",
                  "Level",
                  "Location",
                  "Posted",
                  "Applicants",
                  "Stage",
                ].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((j) => (
                <TableRow
                  key={j.id}
                  sx={{
                    transition: "background 0.15s",
                    "&:hover": { background: "#fafbff" },
                  }}
                >
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {j.title}
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <StatusBadge status={j.dept} />
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        px: 1,
                        py: 0.4,
                        borderRadius: 1,
                        background: "#f1f5f9",
                        color: "#475569",
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {j.level}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 12,
                      color: "#64748b",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                    >
                      <LocationOn sx={{ fontSize: 14 }} />
                      {j.location}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 12,
                      color: "#64748b",
                    }}
                  >
                    {j.posted ? new Date(j.posted).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 13,
                      fontFamily: "'JetBrains Mono',monospace",
                      fontWeight: 600,
                    }}
                  >
                    {j.applicants}
                  </TableCell>
                  <TableCell
                    sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <Chip
                      label={j.stage}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        borderRadius: "99px",
                        background: STAGE_COLORS[j.stage]?.bg,
                        color: STAGE_COLORS[j.stage]?.text,
                        "& .MuiChip-label": { px: 1.2 },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Kanban Board */}
      <Card>
        <Box
          sx={{
            p: "14px 18px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              Candidate Pipeline
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
              {candidates.length} total candidates
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setShowAddCandidate(!showAddCandidate)}
            sx={{ fontSize: 12 }}
          >
            Add Candidate
          </Button>
        </Box>

        {showAddCandidate && (
          <Box sx={{ p: 2, background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Job</InputLabel>
                  <Select
                    value={candidateForm.jobId}
                    label="Select Job"
                    onChange={(e) => setCandidateForm(f => ({ ...f, jobId: e.target.value }))}
                  >
                    {jobs.map(j => (
                      <MenuItem key={j.id} value={(j as any)._id || j.id}>{j.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Candidate Name"
                  value={candidateForm.name}
                  onChange={(e) => setCandidateForm(f => ({ ...f, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Score (0-100)"
                  value={candidateForm.score}
                  onChange={(e) => setCandidateForm(f => ({ ...f, score: Number(e.target.value) }))}
                />
              </Grid>
              <Grid item xs={12} sm={2} sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" onClick={handleAddCandidate} sx={{ flex: 1 }}>Add</Button>
                <Button variant="outlined" onClick={() => setShowAddCandidate(false)}>Cancel</Button>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ p: 2, overflowX: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${STAGES.length},minmax(170px,1fr))`,
              gap: 1.5,
              minWidth: STAGES.length * 180,
            }}
          >
            {STAGES.map((stage) => {
              const stageCandidates = candidates.filter(
                (c) => c.stage === stage,
              );
              const sc = STAGE_COLORS[stage];
              return (
                <Box
                  key={stage}
                  sx={{
                    background: "#f8fafc",
                    borderRadius: 2,
                    overflow: "hidden",
                    minHeight: 200,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: "10px 12px",
                      borderTop: `3px solid ${sc.border}`,
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                      {stage}
                    </Typography>
                    <Box
                      sx={{
                        background: sc.bg,
                        color: sc.text,
                        fontSize: 11,
                        fontWeight: 700,
                        px: 0.8,
                        borderRadius: 99,
                        fontFamily: "'JetBrains Mono',monospace",
                        lineHeight: 1.6,
                      }}
                    >
                      {stageCandidates.length}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      p: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {stageCandidates.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          background: "#fff",
                          borderRadius: 1.5,
                          p: 1.5,
                          border: "1px solid #e5e7eb",
                          transition: "box-shadow 0.15s",
                          "&:hover": {
                            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.3 }}
                        >
                          {c.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11, color: "#94a3b8", mb: 1 }}
                        >
                          {c.role}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              fontFamily: "'JetBrains Mono',monospace",
                              color:
                                c.score >= 90
                                  ? "#16a34a"
                                  : c.score >= 75
                                    ? "#d97706"
                                    : "#dc2626",
                            }}
                          >
                            {c.score}%
                          </Typography>
                          <Box sx={{ display: "flex" }}>
                            <IconButton
                              size="small"
                              onClick={() => moveStage(c.id, -1)}
                              sx={{
                                p: 0.3,
                                color: "#94a3b8",
                                "&:hover": { color: "#85D1DB" },
                              }}
                            >
                              <ChevronLeft sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveStage(c.id, 1)}
                              sx={{
                                p: 0.3,
                                color: "#94a3b8",
                                "&:hover": { color: "#85D1DB" },
                              }}
                            >
                              <ChevronRight sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                    {stageCandidates.length === 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: 60,
                          color: "#cbd5e1",
                          fontSize: 24,
                        }}
                      >
                        —
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default Recruitment;
